/**
 * Design tokens of the admin panel (see DESIGN.md).
 *
 * Every color, radius, shadow and breakpoint used by the UI comes from here, in one set per theme
 * mode. Components never hard-code hex values: they read `theme.custom` (JSS) or the MUI palette,
 * both of which are built from these tokens in `src/theme/index.ts`.
 */
export type ThemeMode = 'light' | 'dark';

/** Semantic meaning of a chip, badge, banner or inline status. */
export type Tone = 'brand' | 'success' | 'warning' | 'danger' | 'info' | 'neutral';

export interface ToneColors {
    /** Solid color: icons, dots, outlined borders, text on the canvas. */
    main: string;
    /** Translucent background for chips and banners. */
    soft: string;
    /** Text on top of `soft` (>= 4.5:1 in both modes). */
    onSoft: string;
    /** Text on top of `main`. */
    contrast: string;
}

export interface DesignTokens {
    mode: ThemeMode;
    color: {
        /** Page background (behind every card). */
        canvas: string;
        /** Dark mode paints a gradient over the canvas; light mode keeps it flat. */
        canvasBackground: string;
        /** Cards and panels that sit on the canvas. */
        surface: string;
        /** Quiet strips inside a card: table heads, filter rows, key/value blocks. */
        surfaceMuted: string;
        /** Opaque surface for things that float: menus, dialogs, sticky table heads, toasts. */
        surfaceRaised: string;
        border: string;
        borderStrong: string;
        text: {
            primary: string;
            secondary: string;
            /** Captions and helper text only, never for data (contrast is below 4.5:1). */
            muted: string;
            inverse: string;
        };
        /** The top bar is dark in both modes. */
        header: { bg: string; text: string; textMuted: string; border: string; hover: string };
        primary: { main: string; hover: string; active: string; soft: string; onSoft: string; contrast: string };
        secondary: { main: string; hover: string; contrast: string };
        tone: Record<Tone, ToneColors>;
        /** Tooltip surface (always dark). */
        tooltip: { bg: string; text: string };
        focusRing: string;
        /** Dialog and drawer backdrop. */
        overlay: string;
        skeleton: string;
        /** One accent gradient, reserved for the brand mark and at most one hero element per page. */
        gradient: { brand: string };
    };
    radius: { xs: number; sm: number; md: number; lg: number; xl: number; pill: number };
    /** 4px scale: space(4) = 16px. */
    space: (n: number) => number;
    shadow: { none: string; sm: string; md: string; lg: string };
    layout: {
        headerHeight: number;
        navWidth: number;
        navWidthMobile: number;
        contentMaxWidth: number;
        pagePadding: number;
        pagePaddingMobile: number;
    };
    /** Media queries for JSS (`[theme.custom.mq.sm]: {...}`). Mobile-first breakpoints, see DESIGN.md. */
    mq: {
        /** Phones, portrait: < 480px */
        xs: string;
        /** Phones + small tablets: < 768px. The side navigation becomes a drawer here. */
        sm: string;
        /** Narrow desktops and tablets, landscape: < 1024px */
        md: string;
        /** Wide desktops: >= 1024px */
        lgUp: string;
        /** Pointer cannot hover (touch): hover-only affordances must be visible by default. */
        touch: string;
    };
    font: { family: string; mono: string };
    motion: { fast: string; base: string; slow: string; ease: string };
}

const FONT_FAMILY = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";
const MONO_FAMILY = "'SF Mono', Menlo, Monaco, Consolas, 'Courier New', monospace";

const shared = {
    radius: { xs: 6, sm: 8, md: 10, lg: 14, xl: 20, pill: 999 },
    space: (n: number) => n * 4,
    layout: {
        headerHeight: 56,
        navWidth: 260,
        navWidthMobile: 280,
        contentMaxWidth: 1880,
        pagePadding: 24,
        pagePaddingMobile: 16,
    },
    mq: {
        xs: '@media (max-width: 479.98px)',
        sm: '@media (max-width: 767.98px)',
        md: '@media (max-width: 1023.98px)',
        lgUp: '@media (min-width: 1024px)',
        touch: '@media (hover: none)',
    },
    font: { family: FONT_FAMILY, mono: MONO_FAMILY },
    motion: { fast: '120ms', base: '180ms', slow: '260ms', ease: 'cubic-bezier(0.2, 0, 0, 1)' },
};

export const lightTokens: DesignTokens = {
    mode: 'light',
    ...shared,
    color: {
        canvas: '#F7F6F2',
        canvasBackground: '#F7F6F2',
        surface: '#FFFFFF',
        surfaceMuted: '#F6F5F1',
        surfaceRaised: '#FFFFFF',
        border: 'rgba(27, 23, 48, 0.10)',
        borderStrong: 'rgba(27, 23, 48, 0.18)',
        text: {
            primary: '#1B1730',
            secondary: '#6B6880',
            muted: '#8E8BA3',
            inverse: '#FFFFFF',
        },
        header: {
            bg: '#2A1A45',
            text: '#FFFFFF',
            textMuted: 'rgba(255, 255, 255, 0.72)',
            border: 'transparent',
            hover: 'rgba(255, 255, 255, 0.10)',
        },
        primary: {
            main: '#6D3FC0',
            hover: '#5B32A6',
            active: '#4E2A8F',
            soft: '#F0E9FB',
            onSoft: '#5B32A6',
            contrast: '#FFFFFF',
        },
        secondary: { main: '#0B7A7A', hover: '#096666', contrast: '#FFFFFF' },
        tone: {
            brand: { main: '#6D3FC0', soft: '#F0E9FB', onSoft: '#5B32A6', contrast: '#FFFFFF' },
            success: { main: '#15804A', soft: '#E3F6EB', onSoft: '#13693C', contrast: '#FFFFFF' },
            warning: { main: '#C27A10', soft: '#FFF2CC', onSoft: '#7A4B00', contrast: '#1B1730' },
            danger: { main: '#D7343F', soft: '#FDE4E5', onSoft: '#9B1B24', contrast: '#FFFFFF' },
            info: { main: '#2A64E0', soft: '#E4ECFC', onSoft: '#1C47A8', contrast: '#FFFFFF' },
            neutral: { main: '#6B6880', soft: '#EEEDEA', onSoft: '#4B4866', contrast: '#FFFFFF' },
        },
        tooltip: { bg: '#1B1730', text: '#FFFFFF' },
        focusRing: '#6D3FC0',
        overlay: 'rgba(27, 23, 48, 0.48)',
        skeleton: 'rgba(27, 23, 48, 0.08)',
        gradient: { brand: 'linear-gradient(135deg, #35C9C1 0%, #8B5CF6 100%)' },
    },
    shadow: {
        none: 'none',
        sm: '0 1px 2px rgba(27, 23, 48, 0.05)',
        md: '0 1px 2px rgba(27, 23, 48, 0.04), 0 6px 20px rgba(27, 23, 48, 0.06)',
        lg: '0 12px 40px rgba(27, 23, 48, 0.16)',
    },
};

export const darkTokens: DesignTokens = {
    mode: 'dark',
    ...shared,
    color: {
        canvas: '#141829',
        canvasBackground: 'linear-gradient(160deg, #131729 0%, #172238 55%, #152033 100%)',
        surface: 'rgba(255, 255, 255, 0.045)',
        surfaceMuted: 'rgba(255, 255, 255, 0.035)',
        surfaceRaised: '#1C2236',
        border: 'rgba(255, 255, 255, 0.09)',
        borderStrong: 'rgba(255, 255, 255, 0.18)',
        text: {
            primary: '#F2F4F8',
            secondary: '#A6AEC2',
            muted: '#717A92',
            inverse: '#1B1730',
        },
        header: {
            bg: '#11152A',
            text: '#F2F4F8',
            textMuted: 'rgba(242, 244, 248, 0.68)',
            border: 'rgba(255, 255, 255, 0.08)',
            hover: 'rgba(255, 255, 255, 0.08)',
        },
        primary: {
            main: '#A98BF0',
            hover: '#BBA3F5',
            active: '#9777E6',
            soft: 'rgba(169, 139, 240, 0.16)',
            onSoft: '#CBB8FA',
            contrast: '#1A1030',
        },
        secondary: { main: '#35C9C1', hover: '#5AD8D1', contrast: '#0C1A1A' },
        tone: {
            brand: { main: '#A98BF0', soft: 'rgba(169, 139, 240, 0.16)', onSoft: '#CBB8FA', contrast: '#1A1030' },
            success: { main: '#3DD68C', soft: 'rgba(61, 214, 140, 0.14)', onSoft: '#7BEBB4', contrast: '#07271A' },
            warning: { main: '#F4B74D', soft: 'rgba(244, 183, 77, 0.14)', onSoft: '#FFD68A', contrast: '#2A1B00' },
            danger: { main: '#F2655F', soft: 'rgba(242, 101, 95, 0.14)', onSoft: '#FFA19C', contrast: '#2A0907' },
            info: { main: '#6AA6FF', soft: 'rgba(106, 166, 255, 0.14)', onSoft: '#B3D0FF', contrast: '#061A3A' },
            neutral: { main: '#A6AEC2', soft: 'rgba(255, 255, 255, 0.08)', onSoft: '#C9CFDD', contrast: '#141829' },
        },
        tooltip: { bg: '#2A3147', text: '#F2F4F8' },
        focusRing: '#A98BF0',
        overlay: 'rgba(5, 8, 20, 0.64)',
        skeleton: 'rgba(255, 255, 255, 0.08)',
        gradient: { brand: 'linear-gradient(135deg, #35C9C1 0%, #8B5CF6 100%)' },
    },
    shadow: {
        none: 'none',
        sm: 'none',
        md: 'none',
        lg: '0 16px 48px rgba(0, 0, 0, 0.55)',
    },
};

export const getTokens = (mode: ThemeMode): DesignTokens => (mode === 'dark' ? darkTokens : lightTokens);
